import { CCol, CFormGroup, CRow } from '@coreui/react';
import moment from 'moment';
import React from 'react';
import { Controller } from 'react-hook-form';
import BuildingsAsyncDropDown from '../../../components/BuildingsAsyncDropDown';
import { CustomDatePicker } from '../../../components/CustomSearchBar';
import { Select, SelectItem } from '../../../components/ui/Select';
import { ErrorText } from '../../../components/utils/AvatarCropper';
import locale from 'antd/es/date-picker/locale/fr_FR';
import localeEn from 'antd/es/date-picker/locale/en_US';
import i18next from 'i18next';
import { InputSeparator, SettingsModalInput } from '../../settings/SettingsModals';
import { FieldLabel } from '../../../components/WizardV2/wizardStylesV2';

const NO_TIME = -1;

function isUnsetStartTime(v) {
  return v == null || v === '' || v === NO_TIME || v === String(NO_TIME);
}

function isAmenitySelected(amenityId) {
  return amenityId != null && amenityId !== '' && parseInt(amenityId, 10) > 0;
}

function AddBlockDetailStepV2({
  t,
  control,
  errors,
  watch,
  amenity,
  amenityDisplayName,
  disabledDates,
  disabledEndDate,
  getFilteredAmenities,
  startTimeOptions,
  endTimeOptions,
  watchStartTime,
  slotsLoading,
  mode = 'add',
  fieldsLocked = false
}) {
  const isEditMode = mode === 'edit';
  const canPickStartDate = isAmenitySelected(watch('amenity_id'));
  const amenitySelectDisabled = isEditMode || !watch('building') || fieldsLocked;
  const startDateLabel = t('Start Date', { defaultValue: 'Start Date' });
  const startTimeLabel = t('Start Time', { defaultValue: 'Start Time' });
  const endDateLabel = t('End Date', { defaultValue: 'End Date' });
  const endTimeLabel = t('End Time', { defaultValue: 'End Time' });
  const startTimeDisabled = !watch('startDate') || slotsLoading;
  const endTimeDisabled = isUnsetStartTime(watchStartTime) || !watch('endDate');
  const showNoEndTimesOnDate =
    !slotsLoading &&
    !endTimeDisabled &&
    !isUnsetStartTime(watchStartTime) &&
    (endTimeOptions?.length ?? 0) === 0;

  return (
    <>
      {!isEditMode ? (
        <InputSeparator>
          <FieldLabel>{t('Select Building', { defaultValue: 'Select Building' })}</FieldLabel>
          <Controller
            control={control}
            name="building"
            render={({ field: { onChange, value } }) => (
              <BuildingsAsyncDropDown
                value={value}
                onChange={onChange}
                valid={!errors.building}
                invalid={errors.building}
              />
            )}
          />
          {errors.building && <ErrorText>{errors.building.message}</ErrorText>}
        </InputSeparator>
      ) : null}

      <InputSeparator>
        <FieldLabel>{t('Select Amenity', { defaultValue: 'Select Amenity' })}</FieldLabel>
        <Controller
          control={control}
          name="amenity_id"
          render={({ field: { onChange, onBlur, value } }) => {
            const editAmenityOptions =
              isEditMode && value != null && value !== ''
                ? [
                    {
                      value,
                      label:
                        amenityDisplayName ||
                        amenity?.description ||
                        getFilteredAmenities?.()?.find((a) => String(a.id) === String(value))
                          ?.description ||
                        t('Amenity')
                    }
                  ]
                : null;

            const amenityOptions = getFilteredAmenities?.() || [];
            const hasAmenities = amenityOptions.length > 0;

            return (
              <Select
                id="block_amenity_id"
                onChange={onChange}
                disabled={amenitySelectDisabled}
                placeholder={isEditMode ? t('Locked in edit mode') : 'Please Select'}
                custom
                onBlur={onBlur}
                invalid={!!errors.amenity_id}
                value={value}
                options={
                  editAmenityOptions ??
                  (hasAmenities
                    ? [
                        {
                          value: -1,
                          label: t('Please select')
                        },
                        ...amenityOptions.map((a) => ({
                          value: a.id,
                          label: a.description
                        }))
                      ]
                    : [
                        {
                          value: null,
                          label: t('No Amenities Available')
                        }
                      ])
                }
              />
            );
          }}
        />
        {errors.amenity_id && <ErrorText>{errors.amenity_id.message}</ErrorText>}
      </InputSeparator>

      <CFormGroup>
        <CRow style={{ alignItems: 'baseline' }}>
          <CCol>
            <InputSeparator>
              <FieldLabel>{startDateLabel}</FieldLabel>
              <Controller
                control={control}
                name="startDate"
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    height={'56px'}
                    locale={i18next.resolvedLanguage === 'fr' ? locale : localeEn}
                    disabled={!canPickStartDate}
                    format="YYYY-MM-DD"
                    onChange={onChange}
                    value={value}
                    disabledDate={amenity && disabledDates}
                  />
                )}
              />
              {errors.startDate && <ErrorText>{errors.startDate.message}</ErrorText>}
            </InputSeparator>
          </CCol>
          <CCol>
            <InputSeparator>
              <FieldLabel>{startTimeLabel}</FieldLabel>
              <Controller
                control={control}
                name="startTime"
                render={({ field: { onChange, onBlur, value } }) => {
                  const currentValueExists =
                    value &&
                    value !== NO_TIME &&
                    startTimeOptions?.some((iso) => iso === value);

                  const shouldShowCurrentValue =
                    !currentValueExists &&
                    value &&
                    value !== NO_TIME &&
                    typeof value === 'string' &&
                    moment.parseZone(value).isValid();

                  return (
                    <Select
                      loading={slotsLoading}
                      onBlur={onBlur}
                      onChange={onChange}
                      disabled={startTimeDisabled}
                      value={value}
                      custom>
                      <SelectItem value={NO_TIME}>{t('Please select')}</SelectItem>
                      {shouldShowCurrentValue && (
                        <SelectItem value={value}>
                          {moment.parseZone(value).format('h:mm a')}
                        </SelectItem>
                      )}
                      {startTimeOptions?.map((iso) => (
                        <SelectItem key={iso} value={iso}>
                          {moment.parseZone(iso).format('h:mm a')}
                        </SelectItem>
                      ))}
                    </Select>
                  );
                }}
              />
              {errors.startTime && !startTimeDisabled && (
                <ErrorText>{errors.startTime.message}</ErrorText>
              )}
            </InputSeparator>
          </CCol>
        </CRow>
      </CFormGroup>

      <CFormGroup>
        <CRow style={{ alignItems: 'baseline' }}>
          <CCol>
            <InputSeparator>
              <FieldLabel>{endDateLabel}</FieldLabel>
              <Controller
                control={control}
                name="endDate"
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    height={'56px'}
                    locale={i18next.resolvedLanguage === 'fr' ? locale : localeEn}
                    disabled={!watch('startDate') || isUnsetStartTime(watchStartTime)}
                    format="YYYY-MM-DD"
                    onChange={onChange}
                    value={value}
                    disabledDate={amenity && disabledEndDate}
                  />
                )}
              />
              {errors.endDate && <ErrorText>{errors.endDate.message}</ErrorText>}
            </InputSeparator>
          </CCol>
          <CCol>
            <InputSeparator>
              <FieldLabel>{endTimeLabel}</FieldLabel>
              <Controller
                control={control}
                name="endTime"
                render={({ field: { onChange, onBlur, value } }) => {
                  const currentValueExists =
                    value &&
                    value !== NO_TIME &&
                    endTimeOptions?.some((iso) => iso === value);

                  const shouldShowCurrentValue =
                    !currentValueExists &&
                    value &&
                    value !== NO_TIME &&
                    typeof value === 'string' &&
                    moment.parseZone(value).isValid();

                  return (
                    <Select
                      loading={slotsLoading && !endTimeDisabled}
                      onBlur={onBlur}
                      onChange={onChange}
                      disabled={endTimeDisabled}
                      value={value}
                      custom>
                      <SelectItem value={NO_TIME}>{t('Please select')}</SelectItem>
                      {shouldShowCurrentValue && (
                        <SelectItem value={value}>
                          {moment.parseZone(value).format('h:mm a')}
                        </SelectItem>
                      )}
                      {endTimeOptions?.map((iso) => {
                        const amPmTime = moment.parseZone(iso).format('h:mm a');
                        return (
                          <SelectItem key={iso} value={iso}>
                            {amPmTime}
                          </SelectItem>
                        );
                      })}
                    </Select>
                  );
                }}
              />
              {showNoEndTimesOnDate && (
                <ErrorText>
                  {t('no_checkout_times_on_date', {
                    defaultValue: 'No checkout times on this date'
                  })}
                </ErrorText>
              )}
              {errors.endTime && <ErrorText>{errors.endTime.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>
      </CFormGroup>

      <CRow></CRow>

      <InputSeparator>
        <FieldLabel>{t('Reason', { defaultValue: 'Reason' })}</FieldLabel>
        <Controller
          control={control}
          name="reason"
          render={({ field: { onChange, onBlur, value } }) => (
            <SettingsModalInput
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              disabled={fieldsLocked}
              valid={!errors.reason}
              error={errors.reason}
              id="block_reason"
              type="text"
              placeholder={t('Enter reason', { defaultValue: 'Enter reason' })}
            />
          )}
        />
        {errors.reason && <ErrorText>{errors.reason.message}</ErrorText>}
      </InputSeparator>
    </>
  );
}

export default AddBlockDetailStepV2;
